<template>
  <v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition" dark>
    <v-card style="background-color: #121212">
      <v-toolbar dark color="rgba(30,30,30,0.9)" flat>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title class="gold-text text-truncate"> Lektionen · {{ module ? module.title : "" }} </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="#58d388" class="black--text" rounded @click="openCreateDialog"> <v-icon left>mdi-plus</v-icon> Lektion hinzufügen </v-btn>
      </v-toolbar>

      <div class="pa-4 pa-md-6">
        <div class="d-flex align-center grey--text text-caption mb-3">
          <v-icon small color="grey" class="mr-1">mdi-information-outline</v-icon>
          Reihenfolge per Drag &amp; Drop ändern
        </div>

        <div v-if="lessons.length === 0 && !loading" class="empty-box lesson-empty-state rounded-xl text-center">
          <span class="lesson-empty-state__icon">
            <v-icon>mdi-book-open-page-variant-outline</v-icon>
          </span>
          <h3>Füge die erste Lektion hinzu</h3>
          <p>Ein Modul braucht mindestens eine Lektion. Starte mit einem Video, Audio, Dokument oder Text.</p>
          <v-btn color="#58d388" class="black--text font-weight-bold" rounded depressed @click="openCreateDialog">
            <v-icon left>mdi-plus</v-icon>
            Erste Lektion erstellen
          </v-btn>
        </div>

        <draggable v-else :list="lessons" handle=".drag-handle" :animation="200" ghost-class="ghost-row" @end="saveOrder" item-key="_id">
          <div v-for="(lesson, index) in lessons" :key="lesson._id" class="lesson-row rounded-lg pa-3 mb-2 d-flex align-center">
            <v-icon class="drag-handle mr-2" color="grey">mdi-drag-vertical</v-icon>
            <div class="lesson-index mr-3">{{ index + 1 }}</div>
            <v-icon :color="typeColor(lesson.contentType)" class="mr-3">{{ typeIcon(lesson.contentType) }}</v-icon>
            <div class="flex-grow-1" style="min-width: 0">
              <div class="font-weight-bold text-truncate">{{ lesson.title }}</div>
              <div class="text-caption grey--text">
                <span class="text-capitalize">{{ lesson.contentType }}</span>
                · {{ lesson.estimatedMinutes || 0 }} Min
              </div>
            </div>
            <v-btn icon small color="blue" @click="editLesson(lesson)"><v-icon small>mdi-pencil</v-icon></v-btn>
            <v-btn icon small color="red" @click="deleteLesson(lesson)"><v-icon small>mdi-delete</v-icon></v-btn>
          </div>
        </draggable>

        <v-progress-linear v-if="loading" indeterminate color="#58d388"></v-progress-linear>
      </div>
    </v-card>

    <!-- Create/Edit Lesson Dialog -->
    <v-dialog
      v-model="editDialog"
      max-width="52rem"
      persistent
      dark
      scrollable
      content-class="lesson-edit-dialog-shell">
      <v-card class="lesson-edit-dialog">
        <header class="lesson-edit-dialog__header">
          <div class="lesson-edit-dialog__title-group">
            <span class="lesson-edit-dialog__mark">
              <v-icon>{{ editingLesson ? "mdi-pencil-outline" : "mdi-book-plus-outline" }}</v-icon>
            </span>
            <div>
              <span class="lesson-edit-dialog__eyebrow">{{ editingLesson ? "Lektion konfigurieren" : "Neuer Lerninhalt" }}</span>
              <h2>{{ editingLesson ? "Lektion bearbeiten" : "Neue Lektion erstellen" }}</h2>
              <p>Titel, Format und Inhalt übersichtlich in drei Schritten erfassen.</p>
            </div>
          </div>
          <v-btn
            icon
            class="lesson-edit-dialog__close"
            aria-label="Lektionsdialog schliessen"
            :disabled="uploading"
            @click="closeEditDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>

        <v-card-text class="lesson-edit-dialog__body">
          <v-form ref="form" v-model="valid">
            <section class="lesson-form-section">
              <div class="lesson-form-section__heading">
                <span class="lesson-form-section__number">1</span>
                <div>
                  <h3>Grunddaten &amp; Format</h3>
                  <p>Benenne die Lektion und entscheide, ob sie aus einer Datei oder aus Text besteht.</p>
                </div>
              </div>

              <v-row dense>
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="form.title"
                    label="Titel"
                    :rules="[(v) => !!v || 'Titel ist erforderlich']"
                    required
                    outlined
                    dense></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.number="form.estimatedMinutes"
                    label="Geschätzte Dauer"
                    suffix="Min"
                    type="number"
                    min="0"
                    outlined
                    dense></v-text-field>
                </v-col>
              </v-row>

              <div class="lesson-mode-heading">
                <h4>Inhaltsformat</h4>
                <p>Wähle die passende Darstellungsform für diese Lektion.</p>
              </div>
              <v-btn-toggle v-model="lessonMode" mandatory class="lesson-mode-toggle" background-color="transparent" dark>
                <v-btn value="media" depressed>
                  <span class="lesson-mode-option__icon"><v-icon>mdi-cloud-upload-outline</v-icon></span>
                  <span class="lesson-mode-option__copy">
                    <strong>Datei oder Medium</strong>
                    <small>Video, Audio, Bild oder Dokument</small>
                  </span>
                  <v-icon class="lesson-mode-option__check">mdi-check-circle</v-icon>
                </v-btn>
                <v-btn value="text" depressed>
                  <span class="lesson-mode-option__icon"><v-icon>mdi-text-box-outline</v-icon></span>
                  <span class="lesson-mode-option__copy">
                    <strong>Textinhalt</strong>
                    <small>Text oder HTML direkt erfassen</small>
                  </span>
                  <v-icon class="lesson-mode-option__check">mdi-check-circle</v-icon>
                </v-btn>
              </v-btn-toggle>

              <template v-if="lessonMode === 'media'">
                <div
                  v-if="
                    form.contentType &&
                    form.contentType !== 'text' &&
                    (mediaFile || (editingLesson && editingLesson.mediaFile && editingLesson.mediaFile.fileId))
                  "
                  class="detected-type">
                  <span class="detected-type__icon">
                    <v-icon :color="typeColor(form.contentType)">{{ typeIcon(form.contentType) }}</v-icon>
                  </span>
                  <div>
                    <span>Erkannter Dateityp</span>
                    <strong class="text-capitalize">{{ form.contentType }}</strong>
                  </div>
                  <v-spacer />
                  <v-btn text class="detected-type__action" @click="manualTypeOverride = !manualTypeOverride">
                    <v-icon left>mdi-tune-variant</v-icon>
                    {{ manualTypeOverride ? "Automatisch erkennen" : "Typ ändern" }}
                  </v-btn>
                </div>
                <v-select
                  v-if="manualTypeOverride"
                  v-model="form.contentType"
                  :items="contentTypeOptions.filter((option) => option.value !== 'text')"
                  item-text="label"
                  item-value="value"
                  label="Dateityp manuell festlegen"
                  outlined
                  dense
                  class="mt-3 mb-0">
                  <template v-slot:selection="{ item }">
                    <v-icon left :color="typeColor(item.value)">{{ typeIcon(item.value) }}</v-icon>
                    {{ item.label }}
                  </template>
                  <template v-slot:item="{ item }">
                    <v-icon left :color="typeColor(item.value)">{{ typeIcon(item.value) }}</v-icon>
                    {{ item.label }}
                  </template>
                </v-select>
              </template>
            </section>

            <section class="lesson-form-section">
              <div class="lesson-form-section__heading">
                <span class="lesson-form-section__number">2</span>
                <div>
                  <h3>Einleitung</h3>
                  <p>Optionaler Kontext, den Nutzer vor dem eigentlichen Inhalt sehen.</p>
                </div>
              </div>

              <v-textarea
                v-model="form.introduction"
                label="Einleitungstext (optional)"
                hint="Zum Beispiel Lernziel, Ablauf oder Vorbereitung"
                persistent-hint
                outlined
                dense
                rows="3"></v-textarea>
            </section>

            <section v-if="!editingLesson" class="lesson-form-section required-lesson-section">
              <div class="lesson-form-section__heading">
                <span class="lesson-form-section__number">3</span>
                <div>
                  <h3>Bestehende Abschlüsse</h3>
                  <p>Lege bewusst fest, ob diese neue Lektion auch für Personen mit bereits abgeschlossenem Modul Pflicht ist.</p>
                </div>
              </div>
              <div class="required-lesson-control" :class="{ active: form.requiredForCompletedUsers }">
                <div>
                  <strong>Für bereits abgeschlossene Personen verpflichtend</strong>
                  <span>
                    Bei Aktivierung wird der Modulabschluss wieder geöffnet, der Fortschritt neu berechnet und die betroffene Lizenz bis zum Abschluss
                    gesperrt. Die Personen erhalten eine Push-Nachricht.
                  </span>
                </div>
                <v-switch
                  v-model="form.requiredForCompletedUsers"
                  color="#58d388"
                  inset
                  hide-details
                  aria-label="Neue Lektion für bereits abgeschlossene Personen verpflichtend machen" />
              </div>
            </section>

            <section class="lesson-form-section">
              <div class="lesson-form-section__heading">
                <span class="lesson-form-section__number">{{ editingLesson ? 3 : 4 }}</span>
                <div>
                  <h3>{{ lessonMode === "media" ? "Datei hochladen" : "Textinhalt erfassen" }}</h3>
                  <p>
                    {{ lessonMode === "media" ? "Eine Datei pro Lektion. Der Dateityp wird automatisch erkannt." : "Der Text wird als eigentlicher Inhalt der Lektion angezeigt." }}
                  </p>
                </div>
              </div>

              <template v-if="lessonMode === 'media'">
                <v-card
                  v-if="editingLesson && editingLesson.mediaFile && editingLesson.mediaFile.fileId && !replacingMedia"
                  class="media-item rounded-xl"
                  dark
                  elevation="0">
                  <div class="media-item__header">
                    <div class="media-icon">
                      <v-icon :color="typeColor(form.contentType)">{{ typeIcon(form.contentType) }}</v-icon>
                    </div>
                    <div class="media-item__copy">
                      <span>Vorhandene Datei</span>
                      <strong>{{ editingLesson.mediaFile.originalName }}</strong>
                      <small>
                        {{ form.contentType }}
                        <template v-if="editingLesson.mediaFile.size"> · {{ formatSize(editingLesson.mediaFile.size) }}</template>
                      </small>
                    </div>
                    <v-btn text class="media-item__replace" @click="replacingMedia = true">
                      <v-icon left>mdi-swap-horizontal</v-icon>
                      Datei ersetzen
                    </v-btn>
                  </div>
                  <div class="media-preview">
                    <SecuredMedia
                      :src="lessonMediaPath(editingLesson)"
                      :media-type="form.contentType"
                      :stream-playback="form.contentType === 'video'"
                      eager />
                  </div>
                </v-card>

                <div
                  v-if="!editingLesson || !editingLesson.mediaFile || !editingLesson.mediaFile.fileId || replacingMedia"
                  class="upload-zone rounded-xl">
                  <div class="upload-zone__intro">
                    <span class="upload-zone__icon"><v-icon>mdi-tray-arrow-up</v-icon></span>
                    <div>
                      <h4>{{ replacingMedia ? "Neue Datei auswählen" : "Lektionsdatei auswählen" }}</h4>
                      <p>Videos bis maximal 5 GB und 60 Minuten. Audio, Bilder und Dokumente bis maximal 250 MB.</p>
                    </div>
                  </div>
                  <v-file-input
                    v-model="mediaFile"
                    :label="replacingMedia ? 'Neue Datei wählen' : 'Datei wählen'"
                    outlined
                    dense
                    show-size
                    :disabled="uploading"
                    prepend-icon="mdi-paperclip"
                    :rules="[(v) => !!v || 'Datei erforderlich', fileSizeRule]"></v-file-input>
                  <div v-if="replacingMedia" class="upload-zone__actions">
                    <v-btn text :disabled="uploading" @click="cancelReplace">Bestehende Datei behalten</v-btn>
                  </div>
                </div>
              </template>

              <v-textarea
                v-else
                v-model="form.textContent"
                label="Inhalt (HTML / Text)"
                hint="Text oder unterstütztes HTML eingeben"
                persistent-hint
                outlined
                rows="8"></v-textarea>
            </section>
          </v-form>

          <div v-if="uploading" class="lesson-upload-progress">
            <div>
              <span>Datei wird hochgeladen</span>
              <strong>{{ uploadProgress }}%</strong>
            </div>
            <v-progress-linear :value="uploadProgress" color="#58d388" background-color="rgba(255,255,255,0.08)" height="8" rounded></v-progress-linear>
          </div>
          <v-alert v-if="saveError" type="error" outlined text class="lesson-save-error mb-0">{{ saveError }}</v-alert>
        </v-card-text>

        <v-card-actions class="lesson-edit-dialog__actions">
          <v-btn text class="lesson-edit-dialog__cancel" :disabled="uploading" @click="closeEditDialog">Abbrechen</v-btn>
          <v-btn
            color="#58d388"
            class="black--text lesson-edit-dialog__save"
            :disabled="!valid || uploading"
            :loading="uploading"
            @click="saveLesson">
            <v-icon left>{{ editingLesson ? "mdi-content-save-outline" : "mdi-check" }}</v-icon>
            {{ editingLesson ? "Änderungen speichern" : "Lektion erstellen" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import api from "@/api"
import { getMediaSizeError, uploadCloudflareMedia } from "@/services/cloudflareMediaUploadService"
import draggable from "vuedraggable"
import SecuredMedia from "../SecuredMedia.vue"

export default {
  name: "LessonEditor",
  components: { draggable, SecuredMedia },
  props: {
    value: Boolean,
    module: Object,
  },
  data() {
    return {
      loading: false,
      lessons: [],
      editDialog: false,
      valid: false,
      editingLesson: null,
      mediaFile: null,
      mediaUploadSession: null,
      mediaUploadComplete: false,
      replacingMedia: false,
      uploading: false,
      uploadProgress: 0,
      saveError: "",
      lessonMode: "media",
      manualTypeOverride: false,
      contentTypeOptions: [
        { value: "video", label: "Video" },
        { value: "audio", label: "Audio" },
        { value: "image", label: "Bild" },
        { value: "pdf", label: "PDF / Dokument" },
        { value: "text", label: "Text / HTML" },
      ],
      form: {
        title: "",
        contentType: "video",
        estimatedMinutes: 5,
        introduction: "",
        textContent: "",
        requiredForCompletedUsers: false,
      },
    }
  },
  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit("input", val)
      },
    },
    fileSizeRule() {
      return (file) => getMediaSizeError(file) || true
    },
  },
  watch: {
    dialog(val) {
      if (val && this.module) this.loadLessons()
    },
    mediaFile(file) {
      const previousSession = this.mediaUploadSession
      this.mediaUploadSession = null
      this.mediaUploadComplete = false
      if (previousSession) this.removePreparedMedia(previousSession)
      // Auto-Detect des Typs aus der MIME – User kann danach trotzdem manuell überschreiben
      if (file && file.type) {
        if (file.type.startsWith("video/")) this.form.contentType = "video"
        else if (file.type.startsWith("audio/")) this.form.contentType = "audio"
        else if (file.type.startsWith("image/")) this.form.contentType = "image"
        else if (file.type === "application/pdf") this.form.contentType = "pdf"
        else this.form.contentType = "file"
      }
    },
    lessonMode(mode) {
      if (mode === "text") this.form.contentType = "text"
      else if (this.form.contentType === "text") this.form.contentType = "video"
    },
  },
  methods: {
    async loadLessons() {
      this.loading = true
      try {
        const mod = await api.akademie.fetchModule(this.module._id || this.module.id)
        this.lessons = (mod && mod.lessons) || []
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    close() {
      this.dialog = false
    },
    closeEditDialog() {
      if (this.uploading) return
      const session = this.mediaUploadSession
      this.mediaUploadSession = null
      this.mediaUploadComplete = false
      this.editDialog = false
      if (session) this.removePreparedMedia(session)
    },
    removePreparedMedia(session) {
      const moduleId = this.module && (this.module._id || this.module.id)
      if (!moduleId || !session) return
      api.akademie.cancelModuleMediaUpload(moduleId, "lesson", session).catch((error) => {
        console.warn("Nicht verwendeter Lektions-Upload konnte nicht entfernt werden", error)
      })
    },
    openCreateDialog() {
      this.editingLesson = null
      this.mediaFile = null
      this.mediaUploadSession = null
      this.mediaUploadComplete = false
      this.replacingMedia = false
      this.lessonMode = "media"
      this.manualTypeOverride = false
      this.saveError = ""
      this.form = {
        title: "",
        contentType: "video",
        estimatedMinutes: 5,
        introduction: "",
        textContent: "",
        requiredForCompletedUsers: false,
      }
      this.editDialog = true
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation()
      })
    },
    cancelReplace() {
      const session = this.mediaUploadSession
      this.mediaUploadSession = null
      this.mediaUploadComplete = false
      this.replacingMedia = false
      this.mediaFile = null
      if (session) this.removePreparedMedia(session)
    },
    formatSize(b) {
      if (!b) return ""
      if (b < 1024) return b + " B"
      if (b < 1024 * 1024) return (b / 1024).toFixed(1) + " KB"
      if (b < 1024 * 1024 * 1024) return (b / 1024 / 1024).toFixed(1) + " MB"
      return (b / 1024 / 1024 / 1024).toFixed(1) + " GB"
    },
    editLesson(item) {
      this.editingLesson = item
      this.mediaFile = null
      this.mediaUploadSession = null
      this.mediaUploadComplete = false
      this.replacingMedia = false
      this.lessonMode = item.contentType === "text" ? "text" : "media"
      this.manualTypeOverride = false
      this.saveError = ""
      this.form = {
        title: item.title || "",
        contentType: item.contentType || "video",
        estimatedMinutes: item.estimatedMinutes || 0,
        introduction: item.introduction || "",
        textContent: item.textContent || "",
        requiredForCompletedUsers: !!item.requiredForCompletedUsers,
      }
      this.editDialog = true
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation()
      })
    },
    async saveLesson() {
      if (!this.$refs.form.validate()) return
      this.uploading = true
      this.uploadProgress = 0
      this.saveError = ""
      try {
        const moduleId = this.module._id || this.module.id
        const payload = { ...this.form }
        if (this.editingLesson) delete payload.requiredForCompletedUsers
        if (this.lessonMode === "text") payload.contentType = "text"
        if (this.mediaFile && (!this.editingLesson || this.replacingMedia)) {
          if (!this.mediaUploadSession) {
            this.mediaUploadSession = await api.akademie.prepareModuleMediaUpload(moduleId, "lesson", this.mediaFile)
          }
          if (!this.mediaUploadComplete) {
            await uploadCloudflareMedia(this.mediaFile, this.mediaUploadSession, {
              onProgress: (loaded, total) => {
                this.uploadProgress = Math.round((loaded * 100) / Math.max(1, total || this.mediaFile.size))
              },
            })
            this.mediaUploadComplete = true
          }
          if (this.mediaUploadSession.provider === "cloudflare_stream") payload.contentType = "video"
          payload.media = this.mediaUploadSession
        }
        if (this.editingLesson) {
          await api.akademie.updateLesson(this.editingLesson._id, payload)
        } else {
          await api.akademie.uploadLesson(moduleId, payload)
        }
        this.mediaUploadSession = null
        this.mediaUploadComplete = false
        this.editDialog = false
        this.loadLessons()
      } catch (e) {
        console.error("Save error", e)
        this.saveError =
          (e.response && e.response.data && (e.response.data.error || e.response.data.message)) ||
          e.message ||
          "Die Lektion konnte nicht gespeichert werden."
      } finally {
        this.uploading = false
      }
    },
    async deleteLesson(item) {
      if (!confirm(`Lektion "${item.title}" wirklich löschen?`)) return
      try {
        await api.akademie.deleteLesson(item._id)
        this.loadLessons()
      } catch (e) {
        console.error(e)
      }
    },
    async saveOrder() {
      const ids = this.lessons.map((l) => l._id)
      try {
        await api.akademie.reorderLessons(this.module._id || this.module.id, ids)
      } catch (e) {
        console.error("Reorder error", e)
        this.loadLessons()
      }
    },
    lessonMediaPath(lesson) {
      return lesson.contentType === "video" ? api.akademie.getLessonPlaybackPath(lesson._id) : api.akademie.getLessonStreamPath(lesson._id)
    },
    typeIcon(t) {
      const map = {
        video: "mdi-video-outline",
        audio: "mdi-music-note-outline",
        image: "mdi-image-outline",
        text: "mdi-text-box-outline",
        pdf: "mdi-file-pdf-box",
        file: "mdi-file-outline",
      }
      return map[t] || "mdi-file-outline"
    },
    typeColor(t) {
      const map = { video: "#ef5350", audio: "#ab47bc", image: "#42a5f5", text: "#66bb6a", pdf: "#e53935", file: "#bdbdbd" }
      return map[t] || "grey"
    },
  },
}
</script>

<style scoped>
.gold-text {
  color: #58d388 !important;
}

.lesson-row {
  background: rgba(30, 30, 30, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: border-color 0.2s, transform 0.2s;
}
.lesson-row:hover {
  border-color: rgba(0, 150, 64, 0.4);
}
.lesson-index {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(0, 150, 64, 0.15);
  color: #58d388;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.85rem;
  flex-shrink: 0;
}
.drag-handle {
  cursor: grab;
}
.drag-handle:active {
  cursor: grabbing;
}
.ghost-row {
  opacity: 0.4;
  background: rgba(0, 150, 64, 0.1) !important;
}
.empty-box {
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(255, 255, 255, 0.08);
}

.lesson-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 42rem;
  margin: 3rem auto;
  padding: 2.5rem 1.5rem;
  border-color: rgba(88, 211, 136, 0.25);
  background: radial-gradient(circle at top, rgba(88, 211, 136, 0.1), transparent 58%), rgba(255, 255, 255, 0.02);
}

.lesson-empty-state__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.75rem;
  height: 3.75rem;
  margin-bottom: 1rem;
  border: 1px solid rgba(88, 211, 136, 0.25);
  border-radius: 1rem;
  background: rgba(88, 211, 136, 0.12);
}

.lesson-empty-state__icon .v-icon {
  color: #7ee5a6;
  font-size: 1.75rem;
}

.lesson-empty-state h3 {
  margin: 0;
  color: #fff;
  font-size: 1.25rem;
  line-height: 1.35;
}

.lesson-empty-state p {
  max-width: 32rem;
  margin: 0.5rem 0 1.25rem;
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.9375rem;
  line-height: 1.55;
}

.lesson-empty-state .v-btn {
  min-height: 2.75rem;
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.875rem;
}

.lesson-edit-dialog {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1.25rem !important;
  background: #1b1c1b !important;
  font-size: 1rem;
}

::v-deep .lesson-edit-dialog-shell {
  border-radius: 1.25rem;
}

.lesson-edit-dialog__header {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem 1.5rem 1.35rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: radial-gradient(circle at top left, rgba(88, 211, 136, 0.13), transparent 42%), #202120;
}

.lesson-edit-dialog__title-group {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  min-width: 0;
}

.lesson-edit-dialog__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  flex: 0 0 3rem;
  border: 1px solid rgba(88, 211, 136, 0.25);
  border-radius: 0.85rem;
  background: rgba(88, 211, 136, 0.12);
}

.lesson-edit-dialog__mark .v-icon {
  color: #7ee5a6;
}

.lesson-edit-dialog__eyebrow {
  display: block;
  margin-bottom: 0.2rem;
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1.4;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.lesson-edit-dialog__title-group h2 {
  margin: 0;
  color: #fff;
  font-size: 1.5rem;
  line-height: 1.25;
}

.lesson-edit-dialog__title-group p {
  margin: 0.35rem 0 0;
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.875rem;
  line-height: 1.5;
}

.lesson-edit-dialog__close {
  flex: 0 0 auto;
  margin-top: -0.25rem;
  margin-right: -0.25rem;
}

.lesson-edit-dialog__body {
  display: grid;
  gap: 1rem;
  padding: 1.25rem 1.5rem 1.5rem !important;
}

.lesson-edit-dialog__body ::v-deep .v-form {
  display: grid;
  gap: 1rem;
}

.lesson-form-section {
  padding: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
}

.lesson-form-section__heading {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1.15rem;
}

.lesson-form-section__number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.8rem;
  height: 1.8rem;
  flex: 0 0 1.8rem;
  border-radius: 50%;
  background: rgba(88, 211, 136, 0.14);
  color: #7ee5a6;
  font-size: 0.8125rem;
  font-weight: 800;
}

.lesson-form-section__heading h3 {
  margin: 0;
  color: #fff;
  font-size: 1.0625rem;
  line-height: 1.35;
}

.lesson-form-section__heading p,
.lesson-mode-heading p {
  margin: 0.2rem 0 0;
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.875rem;
  line-height: 1.5;
}

.required-lesson-section {
  border-color: rgba(245, 158, 11, 0.2);
  background: rgba(245, 158, 11, 0.035);
}

.required-lesson-control {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.16);
}

.required-lesson-control.active {
  border-color: rgba(245, 158, 11, 0.42);
  background: rgba(245, 158, 11, 0.08);
}

.required-lesson-control > div {
  display: grid;
  min-width: 0;
  gap: 0.25rem;
}

.required-lesson-control strong {
  color: #fff;
  font-size: 0.9375rem;
}

.required-lesson-control span {
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.875rem;
  line-height: 1.5;
}

.lesson-mode-heading {
  margin: 0.35rem 0 0.75rem;
}

.lesson-mode-heading h4 {
  margin: 0;
  color: #fff;
  font-size: 0.9375rem;
  line-height: 1.4;
}

.lesson-mode-toggle {
  display: grid !important;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  width: 100%;
  border-radius: 0 !important;
}

.lesson-mode-toggle ::v-deep .v-btn {
  width: 100%;
  min-width: 0 !important;
  min-height: 4.75rem !important;
  height: auto !important;
  padding: 0.75rem 0.85rem !important;
  border: 1px solid rgba(255, 255, 255, 0.09) !important;
  border-radius: 0.8rem !important;
  background: rgba(0, 0, 0, 0.18) !important;
  color: rgba(255, 255, 255, 0.68) !important;
  opacity: 1 !important;
  text-transform: none;
  letter-spacing: 0;
}

.lesson-mode-toggle ::v-deep .v-btn::before {
  opacity: 0 !important;
}

.lesson-mode-toggle ::v-deep .v-btn--active {
  border-color: rgba(88, 211, 136, 0.42) !important;
  background: rgba(88, 211, 136, 0.12) !important;
  color: #fff !important;
}

.lesson-mode-toggle ::v-deep .v-btn__content {
  width: 100%;
  justify-content: flex-start;
  opacity: 1 !important;
}

.lesson-mode-option__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  flex: 0 0 2.5rem;
  margin-right: 0.75rem;
  border-radius: 0.7rem;
  background: rgba(255, 255, 255, 0.05);
}

.lesson-mode-toggle ::v-deep .v-btn--active .lesson-mode-option__icon {
  background: rgba(88, 211, 136, 0.16);
}

.lesson-mode-toggle ::v-deep .v-btn--active .lesson-mode-option__icon .v-icon {
  color: #7ee5a6;
}

.lesson-mode-option__copy {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  line-height: 1.35;
  text-align: left;
}

.lesson-mode-option__copy strong {
  font-size: 0.9375rem;
  white-space: normal;
}

.lesson-mode-option__copy small {
  margin-top: 0.15rem;
  color: rgba(255, 255, 255, 0.52);
  font-size: 0.75rem;
  white-space: normal;
}

.lesson-mode-option__check {
  margin-left: 0.5rem;
  color: #7ee5a6 !important;
  opacity: 0;
}

.lesson-mode-toggle ::v-deep .v-btn--active .lesson-mode-option__check {
  opacity: 1;
}

.detected-type {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-top: 1rem;
  padding: 0.7rem 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 0.75rem;
  background: rgba(0, 0, 0, 0.16);
}

.detected-type__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  flex: 0 0 2.25rem;
  border-radius: 0.6rem;
  background: rgba(255, 255, 255, 0.05);
}

.detected-type > div {
  display: flex;
  flex-direction: column;
}

.detected-type > div > span {
  color: rgba(255, 255, 255, 0.52);
  font-size: 0.75rem;
}

.detected-type > div > strong {
  color: #fff;
  font-size: 0.875rem;
}

.detected-type__action {
  min-height: 2.5rem !important;
  color: rgba(255, 255, 255, 0.68) !important;
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.8125rem;
}

.media-item {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(20, 20, 20, 0.8) !important;
}

.media-item__header {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem;
}

.media-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  flex-shrink: 0;
  border-radius: 0.7rem;
  background: rgba(255, 255, 255, 0.05);
}

.media-item__copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.media-item__copy span,
.media-item__copy small {
  color: rgba(255, 255, 255, 0.52);
  font-size: 0.75rem;
  line-height: 1.4;
}

.media-item__copy strong {
  overflow: hidden;
  margin: 0.15rem 0;
  color: #fff;
  font-size: 0.875rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.media-item__copy small {
  text-transform: uppercase;
}

.media-item__replace {
  min-height: 2.5rem !important;
  color: #7ee5a6 !important;
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.8125rem;
}

.media-preview {
  padding: 0 1rem 1rem;
}

.media-preview ::v-deep .secured-media-content {
  max-height: 20rem;
  border-radius: 0.75rem;
}

.upload-zone {
  padding: 1.25rem;
  border: 1px dashed rgba(88, 211, 136, 0.3);
  background: radial-gradient(circle at top left, rgba(88, 211, 136, 0.08), transparent 48%), rgba(0, 0, 0, 0.12);
}

.upload-zone__intro {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 1rem;
}

.upload-zone__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 2.75rem;
  border-radius: 0.75rem;
  background: rgba(88, 211, 136, 0.13);
}

.upload-zone__icon .v-icon {
  color: #7ee5a6;
}

.upload-zone__intro h4 {
  margin: 0;
  color: #fff;
  font-size: 0.9375rem;
  line-height: 1.4;
}

.upload-zone__intro p {
  margin: 0.15rem 0 0;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.8125rem;
  line-height: 1.4;
}

.upload-zone__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: -0.25rem;
}

.upload-zone__actions .v-btn {
  color: rgba(255, 255, 255, 0.68);
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.8125rem;
}

.lesson-upload-progress {
  padding: 1rem;
  border: 1px solid rgba(88, 211, 136, 0.2);
  border-radius: 0.8rem;
  background: rgba(88, 211, 136, 0.07);
}

.lesson-upload-progress > div {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.6rem;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.875rem;
}

.lesson-upload-progress strong {
  color: #7ee5a6;
}

.lesson-save-error {
  font-size: 0.875rem;
}

.lesson-edit-dialog__actions {
  flex: 0 0 auto;
  gap: 0.5rem;
  padding: 1rem 1.5rem !important;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: #202120;
}

.lesson-edit-dialog__cancel,
.lesson-edit-dialog__save {
  min-height: 2.75rem !important;
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.875rem;
  font-weight: 700;
}

.lesson-edit-dialog__cancel {
  margin-left: auto;
  color: rgba(255, 255, 255, 0.68) !important;
}

.lesson-edit-dialog__save {
  min-width: 13.5rem;
  border-radius: 0.7rem;
}

.lesson-edit-dialog ::v-deep .v-label,
.lesson-edit-dialog ::v-deep input,
.lesson-edit-dialog ::v-deep textarea,
.lesson-edit-dialog ::v-deep .v-select__selection,
.lesson-edit-dialog ::v-deep .v-file-input__text {
  font-size: 0.875rem;
}

.lesson-edit-dialog ::v-deep .v-messages__message,
.lesson-edit-dialog ::v-deep .v-counter {
  font-size: 0.75rem;
  line-height: 1.4;
}

@media (max-width: 37.5rem) {
  .lesson-edit-dialog__header {
    padding: 1.15rem;
  }

  .lesson-edit-dialog__mark {
    width: 2.5rem;
    height: 2.5rem;
    flex-basis: 2.5rem;
  }

  .lesson-edit-dialog__title-group {
    gap: 0.75rem;
  }

  .lesson-edit-dialog__title-group h2 {
    font-size: 1.25rem;
  }

  .lesson-edit-dialog__body {
    padding: 0.85rem !important;
  }

  .lesson-form-section {
    padding: 1rem;
  }

  .lesson-mode-toggle {
    grid-template-columns: 1fr;
  }

  .detected-type {
    flex-wrap: wrap;
  }

  .detected-type .v-spacer {
    display: none;
  }

  .detected-type__action {
    width: 100%;
  }

  .media-item__header {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .media-item__replace {
    grid-column: 1 / -1;
    width: 100%;
  }

  .lesson-edit-dialog__actions {
    display: grid;
    grid-template-columns: 1fr;
    padding: 0.85rem !important;
  }

  .lesson-edit-dialog__cancel {
    order: 2;
    margin-left: 0;
  }

  .lesson-edit-dialog__save {
    min-width: 0;
    width: 100%;
  }
}
</style>
